import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { CircleEvent } from "@/types/circle";

export function useCircleEvents() {
  const { userId } = useAuth();
  
  const { data: events, isLoading, error } = useQuery({
    queryKey: ["circle-events", userId],
    queryFn: async () => {
      const now = new Date().toISOString();
      
      // Fetch upcoming approved events
      const { data: upcomingApproved, error: upcomingError } = await supabase
        .from("circle_events")
        .select(`
          *,
          host:profiles!circle_events_host_id_fkey(
            id,
            first_name,
            last_name,
            email,
            avatar_url
          )
        `)
        .eq("status", "approved")
        .gt("end_time", now)
        .order("start_time", { ascending: true });

      if (upcomingError) throw upcomingError;

      let userPendingEvents: typeof upcomingApproved = [];
      let userHostedPastEvents: typeof upcomingApproved = [];
      let userAttendedPastEventIds: string[] = [];

      if (userId) {
        // Fetch user's own pending events (regardless of time)
        const { data: userPending, error: pendingError } = await supabase
          .from("circle_events")
          .select(`
            *,
            host:profiles!circle_events_host_id_fkey(
              id,
              first_name,
              last_name,
              email,
              avatar_url
            )
          `)
          .eq("host_id", userId)
          .in("status", ["pending", "rejected"])
          .order("start_time", { ascending: true });

        if (pendingError) throw pendingError;
        userPendingEvents = userPending || [];

        // Fetch past events hosted by the user
        const { data: hostedPast, error: hostedPastError } = await supabase
          .from("circle_events")
          .select(`
            *,
            host:profiles!circle_events_host_id_fkey(
              id,
              first_name,
              last_name,
              email,
              avatar_url
            )
          `)
          .eq("host_id", userId)
          .eq("status", "approved")
          .lte("end_time", now)
          .order("start_time", { ascending: false });

        if (hostedPastError) throw hostedPastError;
        userHostedPastEvents = hostedPast || [];

        // Fetch event IDs the user has attended (confirmed bookings for past events)
        const { data: userBookings, error: bookingsError } = await supabase
          .from("circle_bookings")
          .select("circle_event_id")
          .eq("user_id", userId)
          .eq("status", "confirmed");

        if (bookingsError) throw bookingsError;
        userAttendedPastEventIds = userBookings?.map(b => b.circle_event_id) || [];
      }

      // Fetch past events the user attended (but didn't host)
      let userAttendedPastEvents: typeof upcomingApproved = [];
      if (userAttendedPastEventIds.length > 0) {
        const { data: attendedPast, error: attendedError } = await supabase
          .from("circle_events")
          .select(`
            *,
            host:profiles!circle_events_host_id_fkey(
              id,
              first_name,
              last_name,
              email,
              avatar_url
            )
          `)
          .in("id", userAttendedPastEventIds)
          .eq("status", "approved")
          .lte("end_time", now)
          .neq("host_id", userId || "")
          .order("start_time", { ascending: false });

        if (attendedError) throw attendedError;
        userAttendedPastEvents = attendedPast || [];
      }

      // Combine all events and deduplicate
      const allEventsMap = new Map<string, typeof upcomingApproved[0]>();
      
      [...(userPendingEvents || []), ...(upcomingApproved || []), ...(userHostedPastEvents || []), ...(userAttendedPastEvents || [])].forEach(event => {
        allEventsMap.set(event.id, event);
      });

      const allEvents = Array.from(allEventsMap.values());
      const eventIds = allEvents.map(e => e.id);
      
      if (eventIds.length === 0) {
        return [];
      }

      const { data: bookingsData, error: bookingsError } = await supabase
        .from("circle_bookings")
        .select("circle_event_id, quantity")
        .in("circle_event_id", eventIds)
        .eq("status", "confirmed");

      if (bookingsError) throw bookingsError;

      // Calculate booked count per event
      const bookedCounts: Record<string, number> = {};
      bookingsData?.forEach(b => {
        bookedCounts[b.circle_event_id] = (bookedCounts[b.circle_event_id] || 0) + b.quantity;
      });

      // Merge booking counts with events and sort
      const eventsWithCounts = allEvents.map(event => ({
        ...event,
        booked_count: bookedCounts[event.id] || 0,
        spots_remaining: event.capacity - (bookedCounts[event.id] || 0),
      })) as (CircleEvent & { booked_count: number; spots_remaining: number })[];

      // Sort: upcoming first (ascending by start_time), then past (descending by start_time)
      return eventsWithCounts.sort((a, b) => {
        const aIsPast = new Date(a.end_time) <= new Date(now);
        const bIsPast = new Date(b.end_time) <= new Date(now);
        
        if (aIsPast !== bIsPast) {
          return aIsPast ? 1 : -1; // Upcoming events first
        }
        
        if (aIsPast) {
          return new Date(b.start_time).getTime() - new Date(a.start_time).getTime(); // Past: newest first
        }
        return new Date(a.start_time).getTime() - new Date(b.start_time).getTime(); // Upcoming: soonest first
      });
    },
  });

  return {
    events: events || [],
    isLoading,
    error,
  };
}
